import { inject, Service } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  AnalyticsEventsService,
  type GlobalSearchOpener,
} from '@core/providers/analytics/analytics-events.service';

/**
 * Owns the single instance of the palette. Kept out of the component that renders the trigger
 * so the keyboard shortcut and any future entry point open the same dialog.
 */
@Service()
export class GlobalSearchService {
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly analytics = inject(AnalyticsEventsService);

  private dialogRef: MatDialogRef<unknown> | null = null;

  /**
   * In flight while the lazy chunk loads. The single-instance guard has to cover that window
   * too: two shortcut presses in quick succession both cleared a check that only looked at
   * `dialogRef` and stacked two dialogs on top of each other.
   */
  private opening: Promise<void> | null = null;

  async toggle(): Promise<void> {
    // A press that lands mid-open is a double-press, not a request to close again.
    if (this.opening !== null) {
      return;
    }

    if (this.dialogRef) {
      this.close();
      return;
    }

    await this.open('shortcut');
  }

  async open(openedBy: GlobalSearchOpener = 'button'): Promise<void> {
    if (this.dialogRef || this.opening !== null) {
      return;
    }

    this.opening = this.load(openedBy);

    try {
      await this.opening;
    } finally {
      this.opening = null;
    }
  }

  close(): void {
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  private async load(openedBy: GlobalSearchOpener): Promise<void> {
    // Lazily loaded so the palette's markup and styles stay out of the initial shell bundle.
    const { GlobalSearchDialogComponent } = await import('./global-search-dialog.component');

    this.dialogRef = this.dialog.open(GlobalSearchDialogComponent, {
      panelClass: 'fb-search-dialog',
      backdropClass: 'fb-search-backdrop',
      width: '620px',
      maxWidth: 'calc(100vw - 32px)',
      position: { top: '12vh' },
      autoFocus: false,
      restoreFocus: true,
      ariaLabel: this.translateService.instant('SHARED.SYSTEM.SEARCH') as string,
    });

    this.analytics.globalSearchOpened(openedBy);

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }
}
